// src/app/pages/app/WalletPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG as QRCode } from "qrcode.react";
import {
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Send,
  Copy,
  RefreshCw,
  ShieldCheck,
  X,
  AlertTriangle,
  Check,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Crown,
  Coins,
  Bitcoin,
  DollarSign,
  Activity,
  Clock,
  Loader2,
} from "lucide-react";

import {
  getWalletOverview,
  getDepositAddress,
  listWalletTransactions,
  requestWithdraw,
  transferFunds,
  calculatePortfolioValue,
} from "../../features/wallet/walletApi.js";

import { getMarketPrices } from "../../features/markets/marketApi.js";

// Static wallet addresses for display
const STATIC_WALLETS = {
  USDT: {
    address: "TJnTxkm9jg7nwUqxN6VLr9rVLGtjviLhDJ",
    network: "TRON",
    label: "TRON (TRC20)",
    icon: DollarSign,
    color: "from-emerald-500 to-green-400"
  },
  ETH: {
    address: "0xb352912ea076ca608a3b576861bb6e9ceb4fd710",
    network: "MAINNET",
    label: "Ethereum (Mainnet)",
    icon: Coins,
    color: "from-indigo-500 to-purple-400"
  },
  BTC: {
    address: "bc1qvlpzvf0n590vq7vpuawdnvnah8ytt6yumwp0vl",
    network: "MAINNET",
    label: "Bitcoin (Mainnet)",
    icon: Bitcoin,
    color: "from-orange-500 to-amber-400"
  }
};

// ✅ Price change indicator
function PriceChangeIndicator({ changePercent }) {
  if (!changePercent && changePercent !== 0) return null;
  const isPositive = changePercent >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
      {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {isPositive ? '+' : ''}{changePercent.toFixed(2)}%
    </span>
  );
}

function format(n, d = 2) {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) {
    return (0).toLocaleString(undefined, {
      maximumFractionDigits: d,
      minimumFractionDigits: d,
    });
  }
  return num.toLocaleString(undefined, {
    maximumFractionDigits: d,
    minimumFractionDigits: d,
  });
}

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-[#0a0a1a] border border-white/10 shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between bg-white/5">
          <div className="text-white font-semibold flex items-center gap-2">
            <Sparkles size={16} className="text-indigo-400" />
            {title}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/5 border border-white/5 text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            type="button"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// -------------------- Error normalization --------------------
function formatValidationDetail(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    if (detail.length && typeof detail[0] === "object" && detail[0]?.msg) {
      return detail
        .map((e) => {
          const loc = Array.isArray(e.loc) ? e.loc.join(".") : String(e.loc ?? "");
          return loc ? `${loc}: ${e.msg}` : String(e.msg);
        })
        .join(" | ");
    }
    return detail.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join(" | ");
  }
  if (typeof detail === "object") {
    if (detail.msg) return String(detail.msg);
    if (detail.message) return String(detail.message);
    return JSON.stringify(detail);
  }
  return String(detail);
}

function getErrorText(err) {
  if (!err) return "";
  if (typeof err.userMessage === "string" && err.userMessage.trim()) return err.userMessage;
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.message ?? data;
  return formatValidationDetail(detail) || err?.message || "";
}
// -------------------------------------------------------------------------------

const NETWORKS_BY_ASSET = {
  BTC: [{ value: "MAINNET", label: "Bitcoin (Mainnet)" }],
  ETH: [{ value: "MAINNET", label: "Ethereum (Mainnet)" }],
  USDT: [{ value: "TRON", label: "TRON (TRC20)" }],
};

function normalizeAsset(a) {
  return String(a || "").toUpperCase().trim();
}

function normalizeNetwork(n) {
  return String(n || "MAINNET").toUpperCase().trim();
}

function getAllowedNetworks(asset) {
  const a = normalizeAsset(asset);
  return NETWORKS_BY_ASSET[a] || [{ value: "MAINNET", label: "MAINNET" }];
}

function isValidAssetNetwork(asset, network) {
  const a = normalizeAsset(asset);
  const n = normalizeNetwork(network);
  const allowed = getAllowedNetworks(a);
  return allowed.some((x) => x.value === n);
}

function getNetworkLabel(asset, network) {
  const a = normalizeAsset(asset);
  const n = normalizeNetwork(network);
  const allowed = getAllowedNetworks(a);
  return allowed.find((x) => x.value === n)?.label || n;
}

export default function WalletPage() {
  const qc = useQueryClient();

  const [active, setActive] = useState("USDT");
  const [network, setNetwork] = useState("TRON");

  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const [copyMsg, setCopyMsg] = useState("");
  const [copiedField, setCopiedField] = useState(null);

  // Withdraw form
  const [wAmount, setWAmount] = useState("");
  const [wTo, setWTo] = useState("");
  const [wMemo, setWMemo] = useState("");

  // Transfer form
  const [tAmount, setTAmount] = useState("");
  const [tToAccountId, setTToAccountId] = useState("");
  const [tNote, setTNote] = useState("");

  const allowedNetworks = useMemo(() => getAllowedNetworks(active), [active]);

  useEffect(() => {
    const ok = allowedNetworks.some((x) => x.value === network);
    if (!ok) setNetwork(allowedNetworks[0]?.value || "MAINNET");
  }, [allowedNetworks, network]);

  // -------------------- QUERIES --------------------
  const {
    data: overview,
    isLoading: loadingOverview,
    refetch: refetchOverview,
  } = useQuery({
    queryKey: ["walletOverview"],
    queryFn: getWalletOverview,
    staleTime: 10_000,
  });

  // ✅ Fetch market prices for portfolio calculation
  const {
    data: marketPrices,
    isLoading: loadingPrices,
    refetch: refetchPrices,
  } = useQuery({
    queryKey: ["marketPrices"],
    queryFn: getMarketPrices,
    staleTime: 5_000,
    refetchInterval: 10_000, // Auto-refresh every 10 seconds
    retry: 2,
  });

  const balances = useMemo(() => {
    return Array.isArray(overview?.balances) ? overview.balances : [];
  }, [overview]);

  const assets = useMemo(() => {
    const core = ["USDT", "BTC", "ETH"];
    const seen = new Set(core);
    for (const b of balances) {
      if (b?.asset) seen.add(String(b.asset).toUpperCase());
    }
    return Array.from(seen);
  }, [balances]);

  const activeBalance = useMemo(() => {
    const a = normalizeAsset(active);
    const n = normalizeNetwork(network);
    const b = balances.find((x) => x.asset === a && x.network === n);
    return b || { asset: a, network: n, available: 0, locked: 0 };
  }, [balances, active, network]);

  // ✅ Calculate portfolio value using market prices
  const portfolio = useMemo(() => {
    const result = calculatePortfolioValue(balances, marketPrices);
    return result;
  }, [balances, marketPrices]);

  // ✅ Check if prices are being updated
  const isPriceStale = useMemo(() => {
    if (!marketPrices) return true;
    // Check if prices have a timestamp and if it's older than 30 seconds
    if (marketPrices.timestamp) {
      const age = Date.now() - new Date(marketPrices.timestamp).getTime();
      return age > 30000;
    }
    return false;
  }, [marketPrices]);

  // -------------------- Deposit Address Query --------------------
  const canUseCombo = isValidAssetNetwork(active, network);
  const depositEnabled = depositOpen && canUseCombo;

  const {
    data: depositAddr,
    isFetching: fetchingAddr,
    refetch: refetchAddr,
    error: depositErrRaw,
    isError: isDepositError,
  } = useQuery({
    queryKey: ["depositAddress", normalizeAsset(active), normalizeNetwork(network)],
    queryFn: () => getDepositAddress(active, network),
    enabled: depositEnabled,
    staleTime: 30_000,
    retry: 1,
  });

  const depositErr = useMemo(() => getErrorText(depositErrRaw), [depositErrRaw]);

  const staticWalletInfo = useMemo(() => {
    const asset = normalizeAsset(active);
    return STATIC_WALLETS[asset] || null;
  }, [active]);

  const displayAddress = useMemo(() => {
    if (depositEnabled && depositAddr?.address) {
      return depositAddr.address;
    }
    if (staticWalletInfo?.address) {
      return staticWalletInfo.address;
    }
    return null;
  }, [depositEnabled, depositAddr, staticWalletInfo]);

  const qrPayload = useMemo(() => {
    if (depositEnabled && depositAddr?.qrPayload) {
      return depositAddr.qrPayload;
    }
    if (staticWalletInfo?.address) {
      return staticWalletInfo.address;
    }
    return "";
  }, [depositEnabled, depositAddr, staticWalletInfo]);

  const isStaticAddress = useMemo(() => {
    return staticWalletInfo?.address !== null && staticWalletInfo?.address !== undefined;
  }, [staticWalletInfo]);

  const {
    data: txResp,
    isLoading: loadingTxs,
    refetch: refetchTxs,
  } = useQuery({
    queryKey: ["walletTxs", 50, 0],
    queryFn: () => listWalletTransactions({ limit: 50, offset: 0 }),
    staleTime: 5_000,
  });

  const txsItems = useMemo(() => {
    const items = Array.isArray(txResp?.items) ? txResp.items : [];
    return items.filter((t) => {
      const tt = String(t?.type || "").toUpperCase().trim();
      if (!tt) return true;
      const allowedTypes = ["DEPOSIT", "WITHDRAW", "TRANSFER", "TRADE_SETTLEMENT"];
      if (allowedTypes.includes(tt)) return true;
      return false;
    });
  }, [txResp]);

  // -------------------- MUTATIONS --------------------
  const withdrawMut = useMutation({
    mutationFn: requestWithdraw,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["walletOverview"] });
      await qc.invalidateQueries({ queryKey: ["walletTxs"] });
      setWithdrawOpen(false);
      setWAmount("");
      setWTo("");
      setWMemo("");
    },
  });

  const transferMut = useMutation({
    mutationFn: transferFunds,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["walletOverview"] });
      await qc.invalidateQueries({ queryKey: ["walletTxs"] });
      setTransferOpen(false);
      setTAmount("");
      setTToAccountId("");
      setTNote("");
    },
  });

  // -------------------- COMPUTEDS --------------------
  const totalUsdLike = useMemo(() => {
    const usdt = balances
      .filter((b) => b.asset === "USDT")
      .reduce((s, b) => s + Number(b.available || 0) + Number(b.locked || 0), 0);
    return usdt;
  }, [balances]);

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setCopyMsg("Copied!");
      setTimeout(() => {
        setCopyMsg("");
        setCopiedField(null);
      }, 2000);
    } catch {
      setCopyMsg("Copy failed");
      setTimeout(() => setCopyMsg(""), 2000);
    }
  };

  const onRefresh = async () => {
    await Promise.all([refetchOverview(), refetchTxs(), refetchPrices()]);
    if (depositOpen && depositEnabled) await refetchAddr();
  };

  const withdrawErr = useMemo(() => getErrorText(withdrawMut.error), [withdrawMut.error]);
  const transferErr = useMemo(() => getErrorText(transferMut.error), [transferMut.error]);

  // Get asset icon and color
  const getAssetInfo = (asset) => {
    const info = STATIC_WALLETS[normalizeAsset(asset)];
    return info || { icon: Coins, color: "from-gray-500 to-gray-400" };
  };

  // -------------------- UI --------------------
  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-[#0a0a1a] via-[#0f0f2a] to-[#1a0a2e]">
      {/* Decorative background */}
      <div className="fixed top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-indigo-500/30">
              <Wallet size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                Wallet
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-400/20">
                  Secure
                </span>
              </h1>
              <p className="text-white/50 text-sm">Manage your assets, deposits and withdrawals</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-sm">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-emerald-400 font-medium">Live</span>
            </div>
            <button
              onClick={onRefresh}
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition-all duration-300 text-sm flex items-center gap-2 disabled:opacity-50"
              disabled={loadingOverview || loadingTxs}
              type="button"
            >
              <RefreshCw size={16} className={loadingOverview || loadingTxs ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Portfolio Summary - Premium Glass Card */}
        <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 mb-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Crown size={16} className="text-indigo-400" />
                <span className="text-white/40 text-sm">Total Portfolio Value</span>
              </div>
              <div className="flex items-center gap-2">
                {loadingPrices ? (
                  <Loader2 size={14} className="text-white/30 animate-spin" />
                ) : isPriceStale ? (
                  <span className="text-xs text-amber-400/60">Stale</span>
                ) : (
                  <span className="text-xs text-emerald-400/60">Live</span>
                )}
                <span className="text-xs text-white/20">Real-time</span>
              </div>
            </div>

            <div className="text-4xl font-bold text-white">
              {loadingPrices ? (
                <span className="text-2xl text-white/40 animate-pulse flex items-center gap-2">
                  <Loader2 size={24} className="animate-spin" />
                  Loading prices...
                </span>
              ) : (
                `${format(portfolio.total, 2)} USDT`
              )}
            </div>

            {/* Portfolio breakdown */}
            {!loadingPrices && portfolio.breakdown.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-4">
                {portfolio.breakdown.map((item) => (
                  <div key={item.asset} className="flex items-center gap-2 text-sm">
                    <span className="text-white/60">{item.asset}:</span>
                    <span className="text-white font-medium">
                      {format(item.valueInUsdt, 2)} USDT
                    </span>
                    <span className="text-white/30 text-xs">
                      ({format(item.amount, item.asset === 'BTC' ? 6 : 2)} {item.asset})
                    </span>
                    {item.priceInUsdt > 0 && (
                      <span className="text-white/20 text-xs">
                        @ {format(item.priceInUsdt, 2)} USDT
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Asset Selector */}
            <div className="mt-6 flex flex-wrap gap-2">
              {assets.map((asset) => {
                const info = getAssetInfo(asset);
                const isActive = normalizeAsset(active) === normalizeAsset(asset);
                const Icon = info.icon;
                return (
                  <button
                    key={asset}
                    onClick={() => setActive(asset)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all duration-300 ${
                      isActive
                        ? `bg-gradient-to-r ${info.color} bg-clip-text text-transparent border-${info.color.split(' ')[1].split('-')[1] || 'indigo'}-400/40 shadow-lg shadow-${info.color.split(' ')[1].split('-')[1] || 'indigo'}-500/20`
                        : "border-white/10 bg-white/5 text-white/40 hover:text-white hover:bg-white/10"
                    }`}
                    type="button"
                  >
                    <Icon size={16} className={isActive ? `text-${info.color.split(' ')[1].split('-')[1] || 'indigo'}-400` : ""} />
                    {normalizeAsset(asset)}
                  </button>
                );
              })}
            </div>

            {/* Balance Cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                <div className="text-xs text-white/30">Available</div>
                <div className="text-xl font-bold text-white">
                  {format(activeBalance.available, normalizeAsset(active) === "BTC" ? 6 : 2)} {normalizeAsset(active)}
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                <div className="text-xs text-white/30">Locked</div>
                <div className="text-xl font-bold text-white">
                  {format(activeBalance.locked, normalizeAsset(active) === "BTC" ? 6 : 2)} {normalizeAsset(active)}
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                <div className="text-xs text-white/30">Network</div>
                <select
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white outline-none focus:border-indigo-400/50"
                >
                  {allowedNetworks.map((n) => (
                    <option key={n.value} value={n.value}>
                      {n.label}
                    </option>
                  ))}
                </select>
                {!canUseCombo && (
                  <div className="mt-2 text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2 py-2">
                    ⚠️ Invalid network for {normalizeAsset(active)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions & Transactions Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Actions */}
          <div className="col-span-12 lg:col-span-4">
            <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 h-full">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl" />
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-white font-semibold flex items-center gap-2">
                      <Activity size={16} className="text-indigo-400" />
                      Actions
                    </div>
                    <div className="text-xs text-white/30">Manage your funds</div>
                  </div>
                  <ShieldCheck size={16} className="text-emerald-400/60" />
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      if (!canUseCombo) return;
                      setDepositOpen(true);
                    }}
                    disabled={!canUseCombo}
                    className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 shadow-lg shadow-indigo-500/25"
                    type="button"
                  >
                    <ArrowDownToLine size={18} />
                    Deposit
                  </button>

                  <button
                    onClick={() => setWithdrawOpen(true)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold flex items-center justify-center gap-2 transition-all duration-300"
                    type="button"
                  >
                    <ArrowUpFromLine size={18} />
                    Withdraw
                  </button>

                  <button
                    onClick={() => setTransferOpen(true)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold flex items-center justify-center gap-2 transition-all duration-300"
                    type="button"
                  >
                    <Send size={18} />
                    Transfer
                  </button>
                </div>

                <div className="mt-4 text-xs text-white/20 text-center">
                  Withdrawals may require moderator approval
                </div>

                {copyMsg && (
                  <div className="mt-3 text-xs text-emerald-400/80 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
                    <Check size={14} />
                    {copyMsg}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Transactions */}
          <div className="col-span-12 lg:col-span-8">
            <div className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="px-5 py-4 border-b border-white/5 bg-white/5 flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold flex items-center gap-2">
                      <Clock size={16} className="text-indigo-400" />
                      Recent Transactions
                    </div>
                    <div className="text-xs text-white/30">Latest wallet activity</div>
                  </div>
                  <span className="text-xs text-white/20">Live</span>
                </div>

                <div className="divide-y divide-white/5">
                  {txsItems.map((t) => (
                    <div
                      key={t.id}
                      className="px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors group"
                    >
                      <div>
                        <div className="text-sm font-semibold text-white">{t.type}</div>
                        <div className="text-xs text-white/30">
                          {t.asset} • {t.network} •{" "}
                          {t.createdAt ? new Date(t.createdAt).toLocaleString() : "—"}
                        </div>
                        {(t.toAddress || t.txHash) && (
                          <div className="text-xs text-white/20 mt-1 truncate max-w-[320px]">
                            {t.toAddress ? `To: ${t.toAddress}` : ""}
                            {t.txHash ? ` • Tx: ${t.txHash}` : ""}
                          </div>
                        )}
                      </div>

                      <div className="text-right">
                        <div className={`text-sm font-bold ${t.type === "WITHDRAW" ? "text-rose-400" : "text-emerald-400"}`}>
                          {t.type === "WITHDRAW" ? "-" : "+"}
                          {format(t.amount, t.asset === "BTC" ? 6 : 2)} {t.asset}
                        </div>

                        <div
                          className={`text-xs font-bold px-3 py-1 inline-block rounded-full border ${
                            t.status === "COMPLETED"
                              ? "text-emerald-400/80 bg-emerald-500/10 border-emerald-500/20"
                              : t.status === "PENDING"
                              ? "text-amber-400/80 bg-amber-500/10 border-amber-500/20"
                              : "text-rose-400/80 bg-rose-500/10 border-rose-500/20"
                          }`}
                        >
                          {t.status}
                        </div>
                      </div>
                    </div>
                  ))}

                  {!loadingTxs && txsItems.length === 0 && (
                    <div className="px-5 py-12 text-center">
                      <div className="text-5xl mb-4 opacity-30">💳</div>
                      <div className="text-white/30 text-sm">No transactions yet</div>
                      <div className="text-white/20 text-xs mt-1">Your wallet activity will appear here</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit Modal */}
      <Modal
        title={`Deposit ${normalizeAsset(active)}`}
        open={depositOpen}
        onClose={() => setDepositOpen(false)}
      >
        <div className="space-y-4">
          <div className="text-sm text-white/60">
            {isStaticAddress 
              ? `Your ${normalizeAsset(active)} deposit address is shown below. Use this address to receive funds.`
              : "Your deposit address is generated by the backend. Scan QR or copy the address."}
          </div>

          {!canUseCombo && (
            <div className="text-sm text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex gap-2 items-start">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                Invalid asset/network selection for deposits. Select an allowed network for{" "}
                <span className="font-semibold">{normalizeAsset(active)}</span>.
              </div>
            </div>
          )}

          {canUseCombo && isDepositError && !isStaticAddress && (
            <div className="text-sm text-rose-400/80 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
              <div className="font-semibold">Failed to load deposit address</div>
              <div className="mt-1 text-xs text-rose-400/60">
                {depositErr || "Unknown error"}
              </div>
            </div>
          )}

          <div className="rounded-xl bg-white/5 border border-white/10 p-4">
            <div className="text-xs text-white/30">Network</div>
            <div className="text-sm font-semibold text-white">{getNetworkLabel(active, network)}</div>

            <div className="mt-3 text-xs text-white/30">Address</div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="text-sm font-mono text-white/80 truncate">
                {displayAddress || (fetchingAddr ? "Loading…" : "—")}
              </div>
              {displayAddress && (
                <button
                  className={`p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-colors ${
                    copiedField === 'address' ? 'bg-emerald-500/20 border-emerald-500/30' : ''
                  }`}
                  onClick={() => copy(displayAddress, 'address')}
                  title="Copy address"
                  type="button"
                >
                  <Copy size={16} />
                </button>
              )}
            </div>

            {depositAddr?.memo && (
              <div className="mt-3">
                <div className="text-xs text-white/30">Memo/Tag</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="text-sm font-mono text-white/80 truncate">{depositAddr.memo}</div>
                  <button
                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-colors"
                    onClick={() => copy(depositAddr.memo, 'memo')}
                    title="Copy memo"
                    type="button"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-white/30">
                {isStaticAddress ? "Static wallet address" : "QR encodes the backend-provided payload."}
              </div>
              {!isStaticAddress && (
                <button
                  onClick={() => (canUseCombo ? refetchAddr() : null)}
                  className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm transition-colors disabled:opacity-50"
                  disabled={!canUseCombo}
                  type="button"
                >
                  Refresh
                </button>
              )}
            </div>

            {qrPayload && (
              <div className="mt-4 flex justify-center">
                <div className="p-4 rounded-xl bg-white">
                  <QRCode value={qrPayload} size={160} />
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-amber-400/60 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            ⚠️ Only send {normalizeAsset(active)} on {getNetworkLabel(active, network)}. Sending on the wrong network may result in permanent loss.
          </div>
        </div>
      </Modal>

      {/* Withdraw Modal */}
      <Modal
        title={`Withdraw ${normalizeAsset(active)}`}
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            withdrawMut.mutate({
              asset: active,
              network,
              amount: Number(wAmount),
              toAddress: wTo,
              memo: wMemo || undefined,
            });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-xs text-white/40 mb-1">Amount</label>
            <input
              value={wAmount}
              onChange={(e) => setWAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/20 outline-none focus:border-indigo-400/50"
            />
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">To address</label>
            <input
              value={wTo}
              onChange={(e) => setWTo(e.target.value)}
              placeholder="Destination address"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/20 outline-none focus:border-indigo-400/50"
            />
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">Memo (optional)</label>
            <input
              value={wMemo}
              onChange={(e) => setWMemo(e.target.value)}
              placeholder="Memo / Tag"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/20 outline-none focus:border-indigo-400/50"
            />
          </div>

          {withdrawErr && (
            <div className="text-sm text-rose-400/80 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
              {withdrawErr}
            </div>
          )}

          <button
            type="submit"
            disabled={withdrawMut.isPending}
            className="w-full rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all duration-300 font-semibold py-3 text-white shadow-lg shadow-indigo-500/25"
          >
            {withdrawMut.isPending ? "Processing…" : "Submit Withdrawal"}
          </button>

          <div className="text-xs text-white/20 text-center">
            This creates a withdrawal request and locks funds until processed.
          </div>
        </form>
      </Modal>

      {/* Transfer Modal */}
      <Modal
        title={`Transfer ${normalizeAsset(active)}`}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            transferMut.mutate({
              asset: active,
              network,
              amount: Number(tAmount),
              toAccountId: tToAccountId,
              note: tNote || undefined,
            });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-xs text-white/40 mb-1">To Account ID</label>
            <input
              value={tToAccountId}
              onChange={(e) => setTToAccountId(e.target.value)}
              placeholder="Recipient Account ID"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/20 outline-none focus:border-indigo-400/50"
            />
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">Amount</label>
            <input
              value={tAmount}
              onChange={(e) => setTAmount(e.target.value)}
              placeholder="Enter amount"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/20 outline-none focus:border-indigo-400/50"
            />
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">Note (optional)</label>
            <input
              value={tNote}
              onChange={(e) => setTNote(e.target.value)}
              placeholder="Optional note"
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-white placeholder-white/20 outline-none focus:border-indigo-400/50"
            />
          </div>

          {transferErr && (
            <div className="text-sm text-rose-400/80 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
              {transferErr}
            </div>
          )}

          <button
            type="submit"
            disabled={transferMut.isPending}
            className="w-full rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all duration-300 font-semibold py-3 text-white shadow-lg shadow-indigo-500/25"
          >
            {transferMut.isPending ? "Processing…" : "Transfer"}
          </button>

          <div className="text-xs text-white/20 text-center">
            Transfers are internal ledger moves between accounts.
          </div>
        </form>
      </Modal>
    </div>
  );
}
